import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { OrderDto } from '../dto';
import { fetchOrderDetail } from '../api/ordersApi';

interface UseOrderDetailResult {
  order: OrderDto | null;
  setOrder: Dispatch<SetStateAction<OrderDto | null>>;
  loading: boolean;
  error: unknown;
  refresh: () => Promise<OrderDto>;
}

// Background check of the saved status (the card loads the order once, so a
// status moved or reverted by someone else would otherwise stay invisible).
const STATUS_SYNC_INTERVAL_MS = 30000;

export function useOrderDetail(orderId?: string): UseOrderDetailResult {
  const [order, setOrder] = useState<OrderDto | null>(null);
  const [loading, setLoading] = useState(Boolean(orderId));
  const [error, setError] = useState<unknown>(null);

  const refresh = async () => {
    if (!orderId) {
      throw new Error('Order id is required');
    }

    setLoading(true);
    setError(null);

    try {
      const nextOrder = await fetchOrderDetail(orderId, 'order.detail');
      setOrder(nextOrder);
      return nextOrder;
    } catch (nextError) {
      setError(nextError);
      throw nextError;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isActive = true;

    if (!orderId) {
      setOrder(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const nextOrder = await fetchOrderDetail(orderId, 'order.detail');
        if (isActive) {
          setOrder(nextOrder);
        }
      } catch (nextError) {
        if (isActive) {
          setError(nextError);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [orderId]);

  useEffect(() => {
    const changed = (event: Event) => {
      const detail = (event as CustomEvent<{ orderIds?: string[] }>).detail;
      if (detail?.orderIds?.includes(orderId || '')) void refresh().catch(() => undefined);
    };
    window.addEventListener('invoice-workflow-changed', changed);
    return () => window.removeEventListener('invoice-workflow-changed', changed);
    // Refresh current identity only; editor events cannot trigger unrelated reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const statusRef = useRef<string | undefined>(undefined);
  statusRef.current = order?.status;

  useEffect(() => {
    if (!orderId) return undefined;
    let isActive = true;
    let inFlight = false;
    const syncStatus = async () => {
      if (inFlight || document.visibilityState !== 'visible' || statusRef.current === undefined) return;
      inFlight = true;
      const statusAtStart = statusRef.current;
      try {
        const next = await fetchOrderDetail(orderId, 'order.detail');
        // Only a changed status replaces the order: other fields may be mid-edit.
        // A status saved from this card while the read was in flight makes the
        // read older than the card: drop it instead of restoring the old status.
        if (isActive && statusRef.current === statusAtStart && next.status !== statusAtStart) setOrder(next);
      } catch {
        // Silent: the next tick or an explicit action retries.
      } finally {
        inFlight = false;
      }
    };
    const timer = window.setInterval(() => { void syncStatus(); }, STATUS_SYNC_INTERVAL_MS);
    document.addEventListener('visibilitychange', syncStatus);
    window.addEventListener('focus', syncStatus);
    return () => {
      isActive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', syncStatus);
      window.removeEventListener('focus', syncStatus);
    };
  }, [orderId]);

  return {
    order,
    setOrder,
    loading,
    error,
    refresh,
  };
}
